import { create } from "zustand";
import type { ResolutionLabel } from "../resolution/pick-resolution";
import type { CoveredRange } from "./covered-range";
import { mergePoints } from "./merge-points";
import { mergeCoveredRanges } from "./merge-ranges";

export type { CoveredRange };

/**
 * Server-aggregated data point
 *
 * - fetched via (`fetchSingleStreamData`)
 * - keyed by `t` in `mergePoints`
 * - stored verbatim on the cache entry
 * - window-selected on the supply path
 *
 * `t` is in epoch milliseconds so it can be easily de-duped and `prepareSeries`
 * maps it to a `Date` at render time.
 */
export type StreamPoint = {
    t: number;
    avg: number | null;
    min: number | null;
    max: number | null;
    count: number;
};

/**
 * One stream's accumulated data at one resolution, plus which spans are covered.
 *
 * A pure store of heavy bucket data keyed by streamId + resolution. No descriptive
 * metadata lives here — name, unit, and unitCategory are all derived at render (from
 * MasterIndex, the user's preference, and the axis respectively). An accumulate-only cache
 * has no invalidation hook, so a cached copy of any of those would drift from its source;
 * keeping them out keeps the cache honest.
 */
export type CachedStream = {
    streamId: number;
    resolution: ResolutionLabel;
    points: StreamPoint[];
    coveredRanges: CoveredRange[]; // epoch ms, sorted + coalesced
};

type StreamCacheKey = `${number}-${ResolutionLabel}`;

type StreamCache = Record<StreamCacheKey, CachedStream>;

type MergeIntoEntryParams = Omit<CachedStream, "coveredRanges"> & {
    /**
     * The span this data covers. The CALLER (`ensureStreamData`) owns the coverage watermark
     * rule — it passes `[from, cursor]`, extending to `to` only for historical
     * windows. The store records this verbatim; it knows nothing about `now`.
     */
    coveredRange: CoveredRange;
};

type StreamCacheStore = {
    streamCache: StreamCache;
    clear: () => void;
    mergeIntoEntry: (params: MergeIntoEntryParams) => void;
};

export const getStreamCacheKey = (streamId: number, resolution: ResolutionLabel): StreamCacheKey =>
    `${streamId}-${resolution}`;

export const useStreamCacheStore = create<StreamCacheStore>((set, get) => ({
    streamCache: {},
    clear: () => {
        set({ streamCache: {} });
    },
    mergeIntoEntry: ({ streamId, resolution, points, coveredRange }) => {
        const streamCache = get().streamCache;
        const key = getStreamCacheKey(streamId, resolution);
        const existing = streamCache[key];

        const nextEntry: CachedStream = existing
            ? {
                  ...existing,
                  coveredRanges: mergeCoveredRanges(existing.coveredRanges, coveredRange),
                  points: mergePoints(existing.points, points),
              }
            : {
                  streamId,
                  resolution,
                  coveredRanges: [coveredRange],
                  points,
              };

        // Spread copies references, so entries for OTHER keys keep their identity: a
        // selective subscriber (one that reads a single entry or a subset) sees an unchanged
        // reference for streams it didn't touch and won't re-render on this write.
        set({ streamCache: { ...streamCache, [key]: nextEntry } });
    },
}));

/**
 * Non-hook accessor for clearing the stream cache outside React (e.g. the session
 * layer on org switch / logout).
 */
export const clearDataViewerStreamCache = (): void => useStreamCacheStore.getState().clear();
